/**
 * QueryStack — a small stack of queries with a Next/Prev cycler. Used on the
 * Tier 1 report to show a broader sample of "where you appear" or "where you
 * don't" without dumping the full 24-query table before the paywall.
 */
import { useState } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { stripSurroundingQuotes } from "@/lib/text";

export default function QueryStack({
  eyebrow,
  eyebrowColor = "var(--brand-sage-deep)",
  items = [],
  emptyMessage = "No queries in this stack.",
  renderMeta,
  testId,
  icon: Icon,
}) {
  const [i, setI] = useState(0);
  const n = items.length;
  const item = n > 0 ? items[i % n] : null;

  const next = () => setI((v) => (v + 1) % Math.max(n, 1));
  const prev = () => setI((v) => (v - 1 + Math.max(n, 1)) % Math.max(n, 1));

  return (
    <div className="glass rounded-2xl p-6 relative overflow-hidden h-full flex flex-col" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4" style={{ color: eyebrowColor }} />}
        <div className="label-eyebrow" style={{ color: eyebrowColor }}>
          {eyebrow}
        </div>
        {n > 0 && (
          <span className="ml-auto text-xs" style={{ color: "var(--text-muted)" }}>
            {i + 1} / {n}
          </span>
        )}
      </div>

      {!item ? (
        <div className="text-sm italic flex-1 flex items-center" style={{ color: "var(--text-muted)" }}>
          {emptyMessage}
        </div>
      ) : (
        <>
          <div
            key={i}
            className="font-mono text-sm md:text-[15px] leading-relaxed mb-3 fade-in"
            style={{ color: "var(--text-primary)" }}
            data-testid={testId ? `${testId}-query` : undefined}
          >
            &ldquo;{stripSurroundingQuotes(item.query_text)}&rdquo;
          </div>
          {renderMeta && (
            <div className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>
              {renderMeta(item)}
            </div>
          )}
        </>
      )}

      {n > 1 && (
        <div className="mt-auto flex items-center justify-end gap-1 pt-3">
          <button
            onClick={prev}
            className="text-xs flex items-center gap-1 px-2.5 py-1 rounded-md transition-colors"
            style={{ color: "var(--text-secondary)" }}
            aria-label="Previous query"
            data-testid={testId ? `${testId}-prev` : undefined}
          >
            <ChevronLeft className="w-3 h-3" />
            Prev
          </button>
          <button
            onClick={next}
            className="text-xs flex items-center gap-1 px-2.5 py-1 rounded-md transition-colors"
            style={{ color: "var(--brand-sage-deep)", fontWeight: 500 }}
            aria-label="Next query"
            data-testid={testId ? `${testId}-next` : undefined}
          >
            Next
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
